/**
 * @file Renders a search input that debounces by minimum character count
 * and clears on Escape, working around Safari's autofill behavior.
 */
import React, { useState } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const LABEL_SEARCH = 'Search';

type ImperativeResetRef = {
  reset: () => void;
}

type Props = {
  input?: React.MutableRefObject<ImperativeResetRef | null>;
  inputProps?: Omit<
    React.ComponentPropsWithRef<typeof Input>,
    'value' | 'onChange' | 'onKeyDown' | 'prefix' | 'placeholder'
  >;
  onSearch: (value: string) => void;
  minCharacters?: number;
}

/**
 * Renders a search input that calls back once enough characters are
 * typed, and exposes a reset through the given ref.
 */
const SearchInput = ({
  input,
  inputProps = {},
  onSearch,
  minCharacters = 3,
}: Props) => {
  const [searchValue, setSearchValue] = useState<string>('');

  /** Clears the search value and calls back with an empty search. */
  const reset = () => {
    setSearchValue('');
    onSearch('');
  };

  if (input) {
    input.current = input.current || { reset };
    input.current.reset = reset;
  }

  /** Calls back with the typed value once it meets the minimum length. */
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setSearchValue(value);

    const shouldSearch = value.length >= minCharacters || value.length === 0;
    if (shouldSearch) {
      onSearch(value);
      return;
    }

    onSearch('');
  };

  // NOTE: the flag is toggled on the node rather than through state so it is
  //       already gone by the time Safari decides whether to offer AutoFill.
  /** Drops the read-only flag so the input accepts typing after focus. */
  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.readOnly = false;
  };

  /** Restores the read-only flag so the next focus is covered again. */
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.readOnly = true;
  };

  /** Resets the search when the user presses Escape. */
  const onKeyDown = ({ key }: React.KeyboardEvent<HTMLInputElement>) => {
    const isEsc = key === 'Escape';
    
    if (!isEsc) {
      return;
    }

    reset();
  };

  const style: React.CSSProperties = { width: 280 };

  return (
    <Input
      allowClear
      // NOTE: Safari ignores autocomplete="off" and offers SMS verification
      //       codes in any text input that takes focus. It skips read-only
      //       fields, so the input is read-only until it is focused and the
      //       flag is dropped again on blur to keep the next focus covered.
      readOnly
      onFocus={onFocus}
      onBlur={onBlur}
      value={searchValue}
      style={style}
      prefix={<SearchOutlined />}
      onChange={handleSearch}
      onKeyDown={onKeyDown}
      placeholder={LABEL_SEARCH}
      {...inputProps}
    />
  );
};

export default SearchInput;

